import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { IonInput, ModalController } from '@ionic/angular';
import { OrdineVendita } from '../../_models/OrdineVendita';
import { MaterialeOrdine } from '../../_models/MaterialeOrdine';
import { Anagrafica } from '../../_models/Anagrafica';
import { Sede } from '../../_models/Sede';
import { Articolo } from '../../_models/Articolo';
import { VenditaService } from '../../_services/vendita.service';
import { OrganizzazioniService } from '../../_services/organizzazioni.service';
import { ArticoliService } from '../../_services/articoli.service';
import { CampiUtenteService } from '../../_services/campi-utente.service';
import { ArticoloVarianteLocale } from '../../_models/ArticoloVarianteLocale';
import { CampoUtenteDef } from '../../_models/CampoUtenteDef';
import { CampiUtenteFormComponent } from '../../_components/campi-utente-form/campi-utente-form.component';
import { SaveMessage } from '../../_helpers/save-message';
import { MessageConfirm } from '../../_helpers/message-confirm.service';
import { MaterialeOrdineCreateComponent } from '../materiale-ordine-create/materiale-ordine-create.component';
import { DisponibilitaNumero } from '../../_models/DisponibilitaNumero';
import { PianificazioneService } from '../../_services/pianificazione.service';
import { Pianifica, PianificaFilterRequest } from '../../_models/Pianificazione';
import { SelezionaPianificaModalComponent } from '../seleziona-pianifica-modal/seleziona-pianifica-modal.component';
import { DateUtils } from '../../_helpers/date-utils';
// Feature 122 (issue #3377): gate di consenso all'allineamento della pianifica collegata.
import { AllineaPianificaModalComponent } from '../../_components/vendita/allinea-pianifica-modal/allinea-pianifica-modal.component';
import {
  modificheSnapshotPianifica, snapshotPerAllineamento,
} from '../../_helpers/ordine-pianifica-allineamento';

type EsitoVerifica = 'idle' | 'verificando' | 'disponibile' | 'occupato';

const emptyOrdineVendita = (): OrdineVendita => ({
  organizzazioneId: 0,
  idOrdineVendita: 0,
  idRiga: 0,
  annoOrdine: new Date().getFullYear(),
  rifOrdine: '',
  anagraficaId: 0,
  ragioneSocialeCliente: '',
  statoOrdine: 0,
  statoConsegna: 0,
  codiceArticolo: '',
  codiceMateriale: '',
  quantita: 0,
  quantitaUdM2: 0,
  magUdM: '',
  flgTerminato: false,
  note: '',
  isActive: true,
  dataOrdine: new Date(),
  dataCreazione: new Date(),
  dataModifica: new Date()
});

@Component({
  selector: 'app-ordine-vendita-create',
  templateUrl: './ordine-vendita-create.component.html',
  styleUrls: ['./ordine-vendita-create.component.scss'],
  standalone: false
})
export class OrdineVenditaCreateComponent implements OnInit {
  @Input() ordine: OrdineVendita;
  @Input() isNew = true;

  loading = false;

  // Issue #2981 — anteprima NON vincolante del prossimo N° registrazione (solo in creazione).
  // Proprietà DISPLAY-ONLY: NON scrivono mai su ordine.numRegistrazione/ordine.rifOrdine, così il
  // payload di create resta con numerazione null e il worker assegna il numero autoritativo (FR-009/010).
  anteprimaNumRegistrazione: number | null = null;
  anteprimaRifOrdine = '';
  // Guardia anti-spam sul ricalcolo al cambio anno (FR-008): evita richieste ridondanti.
  private anteprimaAnnoCaricato: number | null = null;

  // Issue #3002 — numero/RIF editabili in CREAZIONE + flusso "ordine bis".
  // numRegistrazioneEdit/rifOrdineEdit sono il valore editabile (init dall'anteprima). Il payload di
  // create resta IBRIDO: se non modificato → numRegistrazione null (server assegna, #2981); se
  // modificato o bis generato → valori espliciti (vedi confirm/applicaPayloadNumerazione).
  numRegistrazioneEdit: number | null = null;
  rifOrdineEdit = '';
  numeroModificato = false;
  esitoVerifica: EsitoVerifica = 'idle';
  // Flusso bis (numero occupato): revisione proposta dal backend, valore editabile e conferma.
  bisProposto: number | null = null;
  bisEdit: number | null = null;
  bisConfermato = false;
  esitoVerificaBis: EsitoVerifica = 'idle';
  rifSuggeritoBis: string | null = null;
  // FR-016: true se l'edit manuale del RIF non è parsabile (num/yy[.bis]) → blocca il submit.
  rifNonValido = false;
  // US3: toggle precompilazione campi dall'ordine originale (default suggerito ON).
  precompilaDaOriginale = true;
  // Issue #3002: l'ordine originale viene PRE-CARICATO appena il numero risulta occupato, così
  // confermaBis() è sincrono (niente await) e il bottone "Genera bis" appare solo a dati pronti →
  // elimina la race del doppio click. `ordineBaseCaricato` diventa true SOLO dopo che la GET
  // OrdineBaseByNumero è tornata (200/204/errore): è il gate che abilita il bottone.
  ordineBasePrecompila: OrdineVendita | null = null;
  ordineBaseCaricato = false;
  // Guardia anti-spam sulla verifica on-blur del numero base.
  private ultimoNumeroVerificato: string | null = null;

  // FR-016: parsing del RIF identico a OrdineVenditaPersister.TryParseRifOrdine ("num/yy[.bis]").
  private static readonly RIF_REGEX = /^(\d+)\/(\d{2})(?:\.(\d+))?$/;

  // US6: varianti locali dell'articolo selezionato (selezione facoltativa in ordine)
  variantiLocali: ArticoloVarianteLocale[] = [];

  // ── Materiale ────────────────────────────────────────────────────────────
  materiali: MaterialeOrdine[] = [];
  loadingMateriale = false;

  statiOrdine = [
    { label: 'Indicativo',   value: 70 },
    { label: 'Da confermare',   value: 71 },
    { label: 'Definitivo',  value: 72 },
    { label: 'Sospeso', value: 5 },
    { label: 'Annullato',  value: 73 }
  ];

  statiConsegna = [
    { label: 'In corso', value: 0 },
    { label: 'In preparazione', value: 1 },
    { label: 'Consegnato',      value: 2 },
    { label: 'Annullato',    value: 3 }
  ];

  // Feature 030 — campi utente per organizzazione.
  campiUtenteAttivi: CampoUtenteDef[] = [];
  campiUtenteStorici: CampoUtenteDef[] = [];
  @ViewChild(CampiUtenteFormComponent) private campiUtenteForm?: CampiUtenteFormComponent;

  constructor(
    private venditaService: VenditaService,
    private organizzazioniService: OrganizzazioniService,
    private articoliService: ArticoliService,
    private campiUtenteService: CampiUtenteService,
    private modalCtrl: ModalController,
    private saveMessage: SaveMessage,
    private deleteConfirm: MessageConfirm,
    private pianificazioneService: PianificazioneService
  ) {}

  // Feature 081 (issue #3005): snapshot alla apertura per rilevare il cambio data (granularità
  // giorno) e ricordare se l'ordine era collegato a una pianifica. Lo scollegamento è server-side:
  // il FE non chiama deleteAttivita, si limita a proporre lo spostamento dopo il salvataggio.
  private dataConsegnaSnapshot: string | null = null;
  private eraCollegato = false;
  // Feature 122 (issue #3377): stato dei campi dello snapshot all'apertura della scheda.
  private snapshotAllineamento: OrdineVendita | null = null;
  // Feature 081: destinazione scelta al cambio data (null = scollega); allegata al payload al save.
  private pianificaDestinazioneId: number | null = null;

  // In MODIFICA la data consegna è in sola lettura finché l'operatore non attiva l'edit con la
  // matita, e il controllo sulle pianifiche scatta SOLO alla conferma (✓ / Invio). Prima era
  // agganciato a (ionChange), che su <ion-input type="date"> è un proxy del `change` DOM nativo:
  // su input data quell'evento scatta a OGNI segmento che produce una data completa, quindi la
  // finestra si apriva mentre l'operatore stava ancora componendo la data (01/03 → 02/04 apriva
  // già al cambio del giorno), e sugli stati transitori a valore vuoto non si apriva affatto.
  dataInEdit = false;
  // Valore all'INGRESSO in edit: serve per annullare e per capire se questa sessione ha davvero
  // cambiato qualcosa. Diverso da dataConsegnaSnapshot, che resta il valore a database.
  private dataPrimaEdit: string | null = null;
  // Anti doppio-click sul ✓ mentre l'alert o il selettore pianifica sono già aperti.
  private valutazioneInCorso = false;
  @ViewChild('dataConsegnaInputRef') private dataConsegnaInputRef?: IonInput;

  private async loadCampiUtente() {
    const orgId = this.organizzazioniService.organizzazioneId;
    if (!orgId) return;
    try {
      this.campiUtenteAttivi = await this.campiUtenteService.getDefinizioni(orgId, true);
    } catch {
      this.campiUtenteAttivi = [];
    }
    // In modifica, carico anche quelle disattivate per rendere read-only eventuali valori storici (US4).
    if (!this.isNew && this.ordine?.campiUtente) {
      try {
        const all = await this.campiUtenteService.getDefinizioni(orgId, false);
        const activeCodes = new Set(this.campiUtenteAttivi.map(d => d.codice));
        this.campiUtenteStorici = all.filter(d => !activeCodes.has(d.codice));
      } catch {
        this.campiUtenteStorici = [];
      }
    } else {
      this.campiUtenteStorici = [];
    }
  }

  ngOnInit() {
    if (!this.ordine) {
      this.ordine = emptyOrdineVendita();
      this.ordine.organizzazioneId = this.organizzazioniService.organizzazioneId;
    }
    this.ordine.dataOrdine       = this.toDateString(this.ordine.dataOrdine) as any;
    this.ordine.dataConsegnaRiga = this.toDateString(this.ordine.dataConsegnaRiga) as any;
    // Feature 081: snapshot post-normalizzazione per il confronto a granularità giorno.
    this.dataConsegnaSnapshot = this.ordine.dataConsegnaRiga as any;
    this.eraCollegato = !!this.ordine.attivitaId;
    // Feature 122 (issue #3377): copia dei campi dello snapshot all'apertura, per il diff di US1.
    // ⚠️ Una COPIA, non un riferimento: `this.ordine` viene modificato in luogo dai controlli della
    // scheda, quindi un riferimento renderebbe il confronto sempre vuoto e il dialogo non
    // comparirebbe mai — un difetto silenzioso, proprio della famiglia che questa feature chiude.
    this.snapshotAllineamento = snapshotPerAllineamento(this.ordine);
    if (!this.isNew) {
      this.loadMateriale();
      // US5 (FR-019): in modifica il blocco varianti locali va caricato al load
      // quando l'ordine è collegato a un articolo, non solo alla selezione.
      if (this.ordine.articoloId && this.ordine.articoloId > 0) {
        void this.loadVariantiLocali(this.ordine.articoloId);
      }
    } else {
      void this.caricaAnteprimaNumerazione();
    }
    void this.loadCampiUtente();
  }

  // Issue #2981 — carica l'anteprima del prossimo N° registrazione (solo creazione).
  // try/catch silenzioso come le altre load: un fallimento NON blocca la creazione (FR-012).
  private async caricaAnteprimaNumerazione() {
    const orgId = this.ordine?.organizzazioneId || this.organizzazioniService.organizzazioneId;
    if (!orgId) return;
    const anno = this.ordine?.annoOrdine > 0 ? this.ordine.annoOrdine : new Date().getFullYear();
    try {
      const anteprima = await this.venditaService.getAnteprimaNumRegistrazioneAsync(orgId, anno);
      this.anteprimaNumRegistrazione = anteprima?.numRegistrazione ?? null;
      this.anteprimaRifOrdine        = anteprima?.rifOrdine ?? '';
      this.anteprimaAnnoCaricato     = anteprima?.annoOrdine ?? anno;
    } catch {
      // Stato neutro: nessun errore bloccante, la creazione resta possibile (US3).
      this.anteprimaNumRegistrazione = null;
      this.anteprimaRifOrdine        = '';
    }
    // Issue #3002: se l'operatore non ha personalizzato il numero, adotta la proposta come valore
    // editabile iniziale; altrimenti preserva il numero digitato (es. al cambio anno).
    if (!this.numeroModificato) {
      this.numRegistrazioneEdit = this.anteprimaNumRegistrazione;
      this.rifOrdineEdit        = this.anteprimaRifOrdine;
    }
  }

  // US2/#3002 — al cambio Anno (guardia anti-spam): ricarica la proposta per il nuovo (org, anno) e
  // riesegue la verifica del numero corrente; anno non valido ⇒ fallback all'anno corrente (FR-008).
  async onAnnoChange() {
    if (!this.isNew) return;
    const anno = this.annoEffettivo();
    if (anno === this.anteprimaAnnoCaricato) return;
    await this.caricaAnteprimaNumerazione();
    this.ultimoNumeroVerificato = null;
    this.aggiornaRifDaNumero();
    if (this.numeroModificato) await this.verificaNumeroBase();
    if (this.bisConfermato) await this.onBisChange();
  }

  private annoEffettivo(): number {
    return this.ordine?.annoOrdine > 0 ? this.ordine.annoOrdine : new Date().getFullYear();
  }

  private toDateString(value: any): string | null {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    // Usa componenti locali per evitare lo shift UTC che provocava lo slittamento
    // delle date a ogni open/save del form (mezzanotte locale → giorno prima in UTC).
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // ── Numerazione editabile + flusso bis (issue #3002) ──────────────────────

  // US1 — verifica on-blur del numero base: solo se diverso dalla proposta (payload null = #2981).
  async onNumeroBlur() {
    if (!this.isNew) return;
    const num = this.numRegistrazioneEdit;
    if (num === null || num === undefined) return;
    // Anti doppio-click su "Genera ordine bis": l'ionBlur del numero può scattare DOPO il click sul
    // bottone. Se il numero non è cambiato dall'ultima verifica non resettare lo stato bis, altrimenti
    // il blur annullerebbe la conferma appena fatta (RIF tornerebbe senza .bis). Un numero diverso procede.
    if (`${this.annoEffettivo()}/${num}` === this.ultimoNumeroVerificato) return;
    this.numeroModificato = num !== this.anteprimaNumRegistrazione;
    this.resetStatoBis();
    this.aggiornaRifDaNumero();
    await this.verificaNumeroBase();
  }

  // Verifica disponibilità del numero base (sola lettura). Guard anti-spam sullo stesso (anno, num).
  private async verificaNumeroBase() {
    const orgId = this.ordine?.organizzazioneId || this.organizzazioniService.organizzazioneId;
    const num = this.numRegistrazioneEdit;
    if (!orgId || num === null || num === undefined) return;
    if (!this.numeroModificato) { this.esitoVerifica = 'idle'; return; }
    const chiave = `${this.annoEffettivo()}/${num}`;
    if (chiave === this.ultimoNumeroVerificato && this.esitoVerifica !== 'idle') return;
    this.ultimoNumeroVerificato = chiave;
    this.esitoVerifica = 'verificando';
    try {
      const esito = await this.venditaService.verificaDisponibilitaNumeroAsync(orgId, this.annoEffettivo(), num);
      this.applicaEsitoBase(esito);
    } catch {
      this.esitoVerifica = 'idle';
    }
  }

  private applicaEsitoBase(esito: DisponibilitaNumero) {
    this.esitoVerifica = esito.disponibile ? 'disponibile' : 'occupato';
    this.bisProposto    = esito.disponibile ? null : esito.prossimoBis;
    this.rifSuggeritoBis = esito.disponibile ? null : esito.rifOrdineBis;
    // Numero occupato → PRE-CARICA l'ordine originale, così "Genera bis" appare già con i dati pronti
    // e confermaBis() resta sincrono (no race del doppio click). Disponibile → nessun ordine base.
    if (esito.disponibile) {
      this.ordineBasePrecompila = null;
      this.ordineBaseCaricato = false;
    } else {
      void this.precaricaOrdineBase();
    }
  }

  // Pre-carica la testata dell'ordine base (Bis null) per il numero occupato corrente. Idempotente
  // per numero (chiamato una volta per verifica grazie alla guardia su ultimoNumeroVerificato).
  // ordineBaseCaricato passa a true SOLO nel finally → il bottone si abilita dopo il ritorno della GET.
  private async precaricaOrdineBase() {
    this.ordineBasePrecompila = null;
    this.ordineBaseCaricato = false;
    const orgId = this.ordine?.organizzazioneId || this.organizzazioniService.organizzazioneId;
    const num = this.numRegistrazioneEdit;
    try {
      if (orgId && num !== null && num !== undefined) {
        this.ordineBasePrecompila =
          (await this.venditaService.getOrdineBaseByNumeroAsync(orgId, this.annoEffettivo(), num)) ?? null;
      }
    } catch {
      this.ordineBasePrecompila = null;   // assente/errore: l'operatore procede manualmente
    } finally {
      this.ordineBaseCaricato = true;     // GET tornata (200/204/errore): bottone abilitato
    }
  }

  // US2 — conferma "Genera ordine bis": SINCRONO (nessun await). Precompila dall'ordine già pre-caricato
  // (se toggle attivo), poi imposta bis + RIF. Essendo atomico, nessun blur intermedio può annullarlo.
  confermaBis() {
    if (this.precompilaDaOriginale && this.ordineBasePrecompila) {
      this.precompilaCampiDaBase(this.ordineBasePrecompila);
    }
    this.bisConfermato    = true;
    this.bisEdit          = this.bisProposto;
    this.esitoVerificaBis = 'disponibile';
    this.aggiornaRifDaNumero();
  }

  // US4 — verifica on-blur della coppia (num, bis) quando l'operatore modifica la revisione a mano.
  async onBisChange() {
    if (!this.isNew || !this.bisConfermato) return;
    this.aggiornaRifDaNumero();
    const orgId = this.ordine?.organizzazioneId || this.organizzazioniService.organizzazioneId;
    const num = this.numRegistrazioneEdit;
    const bis = this.bisEdit;
    if (!orgId || num === null || num === undefined || bis === null || bis === undefined) return;
    this.esitoVerificaBis = 'verificando';
    try {
      const esito = await this.venditaService.verificaDisponibilitaNumeroAsync(orgId, this.annoEffettivo(), num, bis);
      this.esitoVerificaBis = esito.disponibile ? 'disponibile' : 'occupato';
      if (!esito.disponibile) {
        this.bisProposto     = esito.prossimoBis;
        this.rifSuggeritoBis = esito.rifOrdineBis;
      }
    } catch {
      this.esitoVerificaBis = 'idle';
    }
  }

  // FR-016 — edit manuale del RIF: ri-deriva la terna (num/anno/bis) dal RIF con la stessa logica di
  // OrdineVenditaPersister.TryParseRifOrdine, così i due indici unici non possono divergere. RIF non
  // parsabile ⇒ rifNonValido = true (submit bloccato in confirm()).
  onRifChange() {
    if (!this.isNew) return;
    const match = OrdineVenditaCreateComponent.RIF_REGEX.exec((this.rifOrdineEdit ?? '').trim());
    if (!match) { this.rifNonValido = true; return; }
    this.rifNonValido = false;
    this.numRegistrazioneEdit = parseInt(match[1], 10);
    this.ordine.annoOrdine    = 2000 + parseInt(match[2], 10);
    const bisParsed = match[3] ? parseInt(match[3], 10) : 0;
    this.bisEdit       = bisParsed > 0 ? bisParsed : null;
    this.bisConfermato = this.bisEdit !== null;
    this.numeroModificato = this.numRegistrazioneEdit !== this.anteprimaNumRegistrazione || this.bisConfermato;
    this.ultimoNumeroVerificato = null;
    this.aggiornaRifDaNumero();
    void (this.bisConfermato ? this.onBisChange() : this.verificaNumeroBase());
  }

  // Ricompone il RIF editabile dalla terna corrente: "num/yy" (base) o "num/yy.bis" (revisione).
  private aggiornaRifDaNumero() {
    const num = this.numRegistrazioneEdit;
    if (num === null || num === undefined) return;
    const yy = (this.annoEffettivo() % 100).toString().padStart(2, '0');
    this.rifOrdineEdit = this.bisConfermato && this.bisEdit
      ? `${num}/${yy}.${this.bisEdit}`
      : `${num}/${yy}`;
    this.rifNonValido = false;
  }

  private resetStatoBis() {
    this.bisConfermato   = false;
    this.bisEdit         = null;
    this.bisProposto     = null;
    this.esitoVerificaBis = 'idle';
    this.rifSuggeritoBis = null;
    this.ordineBasePrecompila = null;
    this.ordineBaseCaricato = false;
  }

  // US3 — copia i SOLI campi di testata dall'ordine base. MAI numerazione/identità/stati/quantità:
  // num/anno/bis/RIF restano quelli del nuovo bis; id/chiavi e stati non vengono ereditati (data-model.md).
  private precompilaCampiDaBase(base: OrdineVendita) {
    this.ordine.anagraficaId          = base.anagraficaId;
    this.ordine.ragioneSocialeCliente = base.ragioneSocialeCliente;
    this.ordine.rifCliente            = base.rifCliente;
    this.ordine.commessa              = base.commessa;
    this.ordine.sedeConsegnaId        = base.sedeConsegnaId;
    this.ordine.zona                  = base.zona;
    this.ordine.colore                = base.colore;
    this.ordine.indirizzoConsegna     = base.indirizzoConsegna;
    this.ordine.indirizzo             = base.indirizzo;
    this.ordine.citta                 = base.citta;
    this.ordine.provincia             = base.provincia;
    this.ordine.cap                   = base.cap;
    this.ordine.nazione               = base.nazione;
    this.ordine.riferimento           = base.riferimento;
    this.ordine.telefono              = base.telefono;
    this.ordine.email                 = base.email;
    this.ordine.rappresentante        = base.rappresentante;
    this.ordine.notaCliente           = base.notaCliente;
    this.ordine.notaConsegna          = base.notaConsegna;
    this.ordine.notaAggiuntiva        = base.notaAggiuntiva;
    this.ordine.valuta                = base.valuta;
    // Campi utente (feature 030): il form figlio rilegge via [value] su ngOnChanges.
    this.ordine.campiUtente           = base.campiUtente ?? null;

    // Issue #3002 (clone completo) — testata ordine: date (formato YYYY-MM-DD come il form), stati,
    // note, importo. Le date passano da toDateString per coerenza con il binding del form.
    this.ordine.dataOrdine        = this.toDateString(base.dataOrdine) as any;
    this.ordine.dataConsegnaRiga  = this.toDateString(base.dataConsegnaRiga) as any;
    this.ordine.statoOrdine       = base.statoOrdine;
    this.ordine.statoConsegna     = base.statoConsegna;
    this.ordine.note              = base.note;
    this.ordine.importoRiga       = base.importoRiga;

    // Riga prodotto completa: articolo/variante, materiale, lotti, quantità, UdM, pesi, magazzino.
    this.ordine.codiceArticolo      = base.codiceArticolo;
    this.ordine.descrizioneArticolo = base.descrizioneArticolo;
    this.ordine.varianteArt         = base.varianteArt;
    this.ordine.articoloId          = base.articoloId ?? null;
    this.ordine.varianteId          = base.varianteId ?? null;
    this.ordine.codiceMateriale     = base.codiceMateriale;
    this.ordine.varianteMat         = base.varianteMat;
    this.ordine.rifLottoProdotto    = base.rifLottoProdotto;
    this.ordine.rifLottoMateriale   = base.rifLottoMateriale;
    this.ordine.quantita            = base.quantita;
    this.ordine.quantitaUdM2        = base.quantitaUdM2;
    this.ordine.udM                 = base.udM;
    this.ordine.udM2                = base.udM2;
    this.ordine.magUdM              = base.magUdM;
    this.ordine.flgTerminato        = base.flgTerminato;
    this.ordine.pesoNetto           = base.pesoNetto;
    this.ordine.pesoLordo           = base.pesoLordo;
    this.ordine.codMagPrincipale    = base.codMagPrincipale;
    this.ordine.codAreaMagPrinc     = base.codAreaMagPrinc;

    // US5: la sorgente dell'id è la sola ordine.articoloId (il selettore la riceve via
    // [articoloId] one-way e la inizializza da [codArt] in caso di valore libero). Nel flusso
    // bis le varianti locali si caricano qui; in modifica lo fa ngOnInit.
    if (this.ordine.articoloId && this.ordine.articoloId > 0) {
      void this.loadVariantiLocali(this.ordine.articoloId);
    }
  }

  // FR-011/FR-016 — blocca il submit quando il numero/revisione scelto non è utilizzabile.
  private validaNumerazione(): boolean {
    if (this.rifNonValido) {
      this.saveMessage.messageError('Rif. ordine non valido per un ordine a numerazione esplicita (formato atteso num/yy[.bis]).');
      return false;
    }
    if (this.esitoVerifica === 'occupato' && !this.bisConfermato) {
      this.saveMessage.messageError('Numero già occupato: generare un ordine bis o scegliere un altro numero.');
      return false;
    }
    if (this.bisConfermato && this.esitoVerificaBis === 'occupato') {
      this.saveMessage.messageError('Revisione bis già occupata: scegliere una revisione libera.');
      return false;
    }
    return true;
  }

  // FR-010 — payload IBRIDO: numero invariato → null (server assegna, #2981); numero modificato o
  // bis generato → valori espliciti. Num/anno/bis sono già coerenti col RIF (vedi onRifChange).
  private applicaPayloadNumerazione() {
    if (!this.numeroModificato && !this.bisConfermato) {
      this.ordine.numRegistrazione = undefined;
      this.ordine.bis              = undefined;
      this.ordine.rifOrdine        = '';
      return;
    }
    this.ordine.numRegistrazione = this.numRegistrazioneEdit ?? undefined;
    this.ordine.annoOrdine       = this.annoEffettivo();
    this.ordine.bis              = this.bisConfermato ? (this.bisEdit ?? undefined) : undefined;
    this.ordine.rifOrdine        = this.rifOrdineEdit;
  }

  // ── Ordine ───────────────────────────────────────────────────────────────

  async confirm() {
    // Edit data ancora aperto: l'operatore ha digitato la nuova data e premuto direttamente
    // "Aggiorna ordine". Si committa qui, così la proposta di spostamento non viene mai saltata.
    if (this.dataInEdit) await this.confermaEditData();

    // FR-007a: serializza i valori dei campi utente solo ora, al salvataggio dell'ordine.
    if (this.campiUtenteForm) {
      this.ordine.campiUtente = this.campiUtenteForm.serialize();
    }

    // Issue #3002 — in creazione: blocchi pre-submit (FR-011/FR-016) + payload ibrido (FR-010).
    if (this.isNew) {
      if (!this.validaNumerazione()) return;
      this.applicaPayloadNumerazione();
    }

    // Feature 081 (issue #3005): la scelta sposta/scollega è già stata presa al cambio data
    // (confermaEditData). La si allega al payload così il backend esegue scollegamento ed
    // eventuale re-link in un'unica sequenza server-side (niente re-link dal FE → niente race, R-010).
    if (!this.isNew && this.eraCollegato && this.richiedeRiprogrammazione())
      this.ordine.pianificaDestinazioneId = this.pianificaDestinazioneId;

    // Feature 122 (issue #3377) — US1: l'operatore decide se allineare anche la pianifica.
    // ⚠️ Il consenso viaggia nel contratto d'ordine e NON in una seconda chiamata (FR-002): il
    // salvataggio non persiste in linea ma accoda su RabbitMQ, quindi una chiamata di propagazione
    // successiva — il pattern delle feature 120 e 121 — sarebbe in corsa con il worker e rileggerebbe
    // valori vecchi.
    this.ordine.allineaPianificaRichiesto = await this.chiediAllineamentoPianifica();

    this.loading = true;
    const op = this.isNew
      ? this.venditaService.createOrdineVenditaAsync(this.ordine)
      : this.venditaService.updateOrdineVenditaAsync(this.ordine);

    op.then((saved: OrdineVendita) => {
        // Flusso asincrono a coda: la persistenza (e la numerazione US5 in creazione) avviene nel
        // worker; scollegamento/spostamento e aggiornamento dei badge "Pianificato" arrivano via
        // SignalR. In creazione il numero/RifOrdine non è ancora disponibile nella risposta.
        this.saveMessage.messageInfo(
          this.isNew
            ? 'Ordine vendita inviato per la creazione. Sarà disponibile a breve.'
            : `Ordine vendita ${saved.rifOrdine} aggiornato.`
        );
        this.modalCtrl.dismiss(saved, 'confirm');
      })
      .catch(() => this.saveMessage.messageError('Ci sono problemi con il salvataggio.'))
      .finally(() => this.loading = false);
  }

  // Feature 122 (issue #3377) — US1, FR-001/FR-006/FR-007/FR-008.
  //
  // Apre il dialogo di allineamento SOLO se ricorrono tutte e tre le condizioni:
  //
  //   1. l'ordine è COLLEGATO a un'attività — su un ordine non pianificato non c'è nulla da
  //      allineare, e in creazione non può esistere un collegamento (FR-007);
  //   2. almeno un campo dello SNAPSHOT è cambiato — salvare senza aver toccato nulla di rilevante
  //      per la pianifica non deve far comparire alcun dialogo (FR-007);
  //   3. NON c'è riprogrammazione in corso — se la data di consegna cambia fra due date valorizzate
  //      vale la sola scelta di SPOSTAMENTO già esistente, e le due condizioni sono MUTUAMENTE
  //      ESCLUSIVE (FR-006): l'attività verrà eliminata o ricreata nella pianifica di destinazione,
  //      dove nasce allineata per costruzione. Chiedere anche l'allineamento dei dati sarebbe una
  //      seconda domanda sullo stesso salvataggio, su un'attività che sta per essere sostituita.
  //
  // ⚠️ IL SILENZIO VALE «SÌ», NON «NO», e la distinzione è la correzione più importante di questo
  // gate. `false` deve significare UNA sola cosa: l'operatore ha visto la domanda e ha risposto di
  // no. Quando la domanda NON viene posta, il comportamento deve restare quello di sempre —
  // allineare — perché il diff copre 30 campi mentre il messaggio ne porta ~60, e i restanti la
  // pianifica LI LEGGE: `rappresentante`, `numRegistrazione`, `bis`, `statoOrdine`, `statoConsegna`,
  // `flgTerminato`, `importoRiga`, `valuta`, `dataOrdine`, `pesoNetto`, `pesoLordo`,
  // `codMagPrincipale` sono tutti letti dai column provider di Pianificazione e nessuno di essi è in
  // CAMPI_SNAPSHOT_PIANIFICA. Con il default a `false`, modificare SOLO il Rappresentante di un
  // ordine pianificato non aggiornava più la pianifica, senza dialogo e senza errore: il difetto
  // silenzioso che questa feature esiste per chiudere, reintrodotto dal suo stesso gate.
  //
  // I quattro rami che restituiscono `true` senza chiedere nulla sono innocui a valle:
  //   • creazione / ordine non collegato → `attivita.Count == 0`, uscita anticipata in Planning;
  //   • riprogrammazione → il ramo strutturale esce PRIMA del gate, che non viene nemmeno letto;
  //   • nessuna modifica rilevata → riscrive uno snapshot identico e non rigeocodifica
  //     (`indirizzoCambiato` è falso).
  //
  // ⚠️ `richiedeRiprogrammazione()` è il mirror esatto della regola server e NON va modificato qui:
  // è ciò che rende le due condizioni davvero mutuamente esclusive invece che solo apparentemente.
  //
  // ⚠️ Annullare o chiudere il dialogo senza scegliere vale «non allineare» (FR-008) e non annulla
  // il salvataggio: la chiusura restituisce `data` indefinito, che qui diventa `false`. È il
  // solo ramo in cui l'assenza di una risposta esplicita significa «no», ed è corretto: lì la
  // domanda è stata posta.
  private async chiediAllineamentoPianifica(): Promise<boolean> {
    if (this.isNew || !this.eraCollegato) return true;
    if (this.richiedeRiprogrammazione()) return true;
    if (!this.snapshotAllineamento) return true;

    const modifiche = modificheSnapshotPianifica(this.snapshotAllineamento, this.ordine);
    if (modifiche.length === 0) return true;

    const modal = await this.modalCtrl.create({
      component: AllineaPianificaModalComponent,
      componentProps: { modifiche },
      cssClass: 'propaga-ordini-modal',
    });
    await modal.present();

    // ⚠️ `onDidDismiss`, NON `onWillDismiss`, e la differenza qui vale la chiusura della scheda.
    //
    // Questo è l'UNICO dialogo che si apre DENTRO `confirm()`, con un `modalCtrl.dismiss()` della
    // scheda ordine subito a valle. In Ionic 7 `dismiss()` senza id colpisce l'overlay presentato
    // più in alto, e un overlay resta «presentato» per tutta l'animazione di uscita: `willDismiss`
    // viene emesso PRIMA dell'animazione, mentre la classe `overlay-hidden` — l'unica cosa che lo
    // toglie da `getPresentedOverlays` — viene aggiunta DOPO (`@ionic/core/components/overlays.js`).
    //
    // Con `onWillDismiss` la sequenza era: il dialogo comincia a chiudersi → il salvataggio parte e
    // finisce (è una 202, torna subito) → `this.modalCtrl.dismiss(saved, 'confirm')` trova ANCORA il
    // dialogo di allineamento in cima → `dismiss` su un overlay con `presented === false` esce dalla
    // guardia `if (!overlay.presented) return false` → **nessun errore, nessun log, e la scheda
    // ordine non si chiude**. Il salvataggio riusciva, il messaggio compariva, la finestra restava.
    //
    // `onDidDismiss` si risolve dopo l'animazione, e le due righe che seguono l'emissione
    // (`classList.add('overlay-hidden')` e `el.remove()`) girano in modo sincrono prima che il
    // nostro `await` riprenda: quando la scheda chiama il proprio dismiss, il dialogo non è più fra
    // gli overlay presentati.
    //
    // Gli altri due dialoghi della scheda (selettore pianifica, materiale) non hanno mai avuto il
    // problema perché nascono da un'interazione dell'utente e non da `confirm()`: fra la loro
    // chiusura e il salvataggio c'è sempre di mezzo una persona.
    const { data } = await modal.onDidDismiss<boolean>();
    return data === true;
  }

  // Mirror ESATTO di OrdiniVenditaService.RichiedeRiprogrammazione (feature 115, FR-008/009/010):
  // il server riprogramma SOLO fra due date entrambe valorizzate e in giorni diversi. Prima qui
  // bastava `snapshot !== corrente`, quindi su null→data e data→null il frontend apriva la finestra
  // e faceva scegliere una pianifica che il server poi ignorava: una promessa non mantenuta.
  private richiedeRiprogrammazione(): boolean {
    const precedente = this.toDateString(this.dataConsegnaSnapshot);
    const nuova      = this.toDateString(this.ordine.dataConsegnaRiga);
    return !!precedente && !!nuova && precedente !== nuova;
  }

  // ── Ciclo di modifica esplicita della data consegna ────────────────────────────────────────

  apriEditData() {
    if (this.dataInEdit) return;
    this.dataPrimaEdit = (this.ordine.dataConsegnaRiga as any) ?? null;
    this.dataInEdit = true;
    // Focus programmatico come in risorse-update: l'operatore ha già espresso l'intento cliccando.
    setTimeout(() => this.dataConsegnaInputRef?.setFocus(), 0);
  }

  // Il ripristino non è cosmetico: ngModel su ion-input passa da `ionInput`, quindi il modello è
  // già sporcato a ogni segmento digitato — incluso il transitorio a valore vuoto.
  annullaEditData() {
    this.ordine.dataConsegnaRiga = this.dataPrimaEdit as any;
    this.dataInEdit = false;
  }

  // UNICO punto che fa scattare il controllo sulle pianifiche.
  async confermaEditData() {
    if (this.valutazioneInCorso) return;
    this.dataInEdit = false;
    if (!this.dataModificataInSessione()) return;
    await this.valutaDestinazionePianifica();
  }

  private dataModificataInSessione(): boolean {
    return this.toDateString(this.dataPrimaEdit) !== this.toDateString(this.ordine.dataConsegnaRiga);
  }

  // Feature 081 (issue #3005): decide la destinazione (sposta in una pianifica con la nuova data,
  // oppure scollega). La scelta è allegata al payload al salvataggio; scollegamento ed eventuale
  // re-link sono eseguiti server-side (R-010).
  //
  // NON azzera più `pianificaDestinazioneId` in testa: prima lo faceva a ogni evento di digitazione
  // e cancellava una scelta già fatta dall'operatore. Ora si azzera solo quando la data non
  // richiede più riprogrammazione, cioè quando non c'è nulla da propagare.
  private async valutaDestinazionePianifica() {
    if (this.isNew || !this.eraCollegato) return;
    if (!this.richiedeRiprogrammazione()) { this.pianificaDestinazioneId = null; return; }
    this.valutazioneInCorso = true;
    try { await this.proponiSpostamentoPianifica(this.ordine.dataConsegnaRiga as any); }
    finally { this.valutazioneInCorso = false; }
  }

  // Dispatch 0/1/>1 pianifiche per la nuova data → memorizza la destinazione scelta (null = scollega).
  private async proponiSpostamentoPianifica(nuovaData: string) {
    // Nessun guard sul valore vuoto: `richiedeRiprogrammazione()` garantisce già una data
    // valorizzata. Prima il guard inghiottiva in silenzio gli stati transitori dell'input data,
    // ed è il motivo per cui cambiando solo il giorno non si apriva nulla.
    const items = await this.cercaPianifichePerData(nuovaData);
    if (items === null) return;                                    // errore già notificato
    if (items.length === 0) {
      this.saveMessage.messageInfo("Nessuna pianifica con la nuova data: al salvataggio l'ordine verrà scollegato.");
      return;
    }
    const scelta = items.length === 1
      ? (await this.confermaSpostamento(items[0]) ? items[0] : null)
      : await this.apriSelettorePianifica(items);
    this.pianificaDestinazioneId = scelta?.pianificaId ?? null;
  }

  // getPianifiche con PianificaFilterRequest COMPLETO (count = 0 → tutte le pianifiche del giorno),
  // senza filtro stato (qualsiasi stato è destinazione valida — FR-006). Ritorna null su errore.
  private async cercaPianifichePerData(nuovaData: string): Promise<Pianifica[] | null> {
    const giorno = this.parseGiornoLocale(nuovaData);
    // `[]` significa «nessuna pianifica per quella data» e produce il messaggio «verrà scollegato»:
    // una data non parsabile è un errore, non quell'esito. Va distinta, altrimenti un problema
    // viene comunicato all'operatore come una conseguenza normale.
    if (!giorno) {
      this.saveMessage.messageError('Data consegna non valida.');
      return null;
    }
    const request: PianificaFilterRequest = {
      orgId: this.ordine.organizzazioneId || this.organizzazioniService.organizzazioneId,
      page: 0, count: 0, sortField: 'DataPrevista', sortAsc: true,
      // Range di GIORNATA (giorno locale): DataPrevista è un datetime2 CON orario, quindi un singolo
      // istante (dataDal == dataAl == mezzanotte) non matcherebbe le pianifiche del giorno. (review PR #3006)
      dataDal: DateUtils.startOfDayLocal(giorno),
      dataAl:  DateUtils.endOfDayLocal(giorno)
    };
    try {
      // SearchResult<T> espone i risultati nel campo `result`; il dispatch 0/1/>1 è su result.result.
      const result = await this.pianificazioneService.getPianifiche(request);
      return result?.result ?? [];
    } catch {
      this.saveMessage.messageError('Impossibile verificare le pianifiche per la nuova data.');
      return null;
    }
  }

  // Interpreta la data (yyyy-MM-dd da input type="date") come giorno LOCALE: `new Date("yyyy-MM-dd")`
  // sarebbe mezzanotte UTC e in fusi negativi cadrebbe nel giorno precedente.
  private parseGiornoLocale(value: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
    if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  // Caso 1 pianifica: conferma diretta (FR-008). true = sposta, false = scollega (FR-011).
  private async confermaSpostamento(pianifica: Pianifica): Promise<boolean> {
    const dataStr = this.toDateString(pianifica.dataPrevista);
    const descr = pianifica.descrizione || pianifica.nome;
    return this.deleteConfirm.presentAlert(
      `Esiste una pianifica del ${dataStr} — ${descr}. Spostare l'ordine?`, () => {});
  }

  // Caso >1 pianifiche: selettore (FR-009). Ritorna la pianifica scelta o null = scollega (FR-011).
  private async apriSelettorePianifica(pianifiche: Pianifica[]): Promise<Pianifica | null> {
    const modal = await this.modalCtrl.create({
      component: SelezionaPianificaModalComponent,
      componentProps: { pianifiche },
      cssClass: 'custom-modal'
    });
    await modal.present();
    const { data, role } = await modal.onWillDismiss<Pianifica>();
    return role === 'confirm' && data ? data : null;
  }

  cancel = () => this.modalCtrl.dismiss(false, 'cancel');

  // ── Autocompilazione da Anagrafica / Sede ─────────────────────────────────

  onArticoloProdottoSelezionato(articolo: Articolo) {
    if (!articolo) {
      this.ordine.codiceArticolo = '';
      this.ordine.varianteArt    = '';
      this.ordine.articoloId     = null;
      this.ordine.varianteId     = null;
      this.ordine.udM            = '';
      this.ordine.udM2           = '';
      this.ordine.magUdM         = '';
      this.variantiLocali        = [];
      return;
    }
    this.ordine.codiceArticolo = articolo.codArt   ?? '';
    this.ordine.varianteArt    = articolo.varianteArt ?? '';
    this.ordine.articoloId     = articolo.id ?? null;   // US6: rif. articolo per la variante
    this.ordine.varianteId     = null;                  // azzera la variante alla selezione articolo
    this.ordine.udM            = articolo.umMag    ?? '';
    this.ordine.udM2           = articolo.umMag2   ?? '';
    this.ordine.magUdM         = articolo.umMag    ?? '';
    this.loadVariantiLocali(articolo.id);
  }

  // US4: valore libero dal selettore → codice non a catalogo. Azzera l'articolo collegato e i
  // dati derivati da una selezione precedente (varianteArt resta, è un campo libero indipendente).
  onCodArtLibero(testo: string) {
    this.ordine.codiceArticolo = testo;
    this.ordine.articoloId     = null;
    this.ordine.varianteId     = null;
    this.ordine.udM            = '';
    this.ordine.udM2           = '';
    this.ordine.magUdM         = '';
    this.variantiLocali        = [];
  }

  // US6: carica le varianti locali dell'articolo (sostituisce la fonte ESOLVER per la selezione in ordine)
  private async loadVariantiLocali(articoloId?: number) {
    this.variantiLocali = [];
    if (!articoloId) return;
    try {
      const res = await this.articoliService.getVariantiByArticolo(this.organizzazioniService.organizzazioneId, articoloId);
      this.variantiLocali = res?.result ?? [];
    } catch {
      this.variantiLocali = [];
    }
  }

  // US6: selezione (facoltativa) di una variante locale → memorizza varianteId + varianteArt
  onVarianteLocaleSelezionata(varianteId: number | string | null | undefined) {
    const normalizedVarianteId = typeof varianteId === 'string'
      ? Number(varianteId)
      : varianteId;
    this.ordine.varianteId = Number.isFinite(normalizedVarianteId) ? normalizedVarianteId : null;
    const v = this.variantiLocali.find(x => x.varianteId === this.ordine.varianteId);
    this.ordine.varianteArt = v?.varianteArt ?? '';
  }

  onAnagraficaSelezionata(anagrafica: Anagrafica) {
    if (!anagrafica) {
      this.ordine.ragioneSocialeCliente = '';
      return;
    }
    // Feature 087 (issue #3055) — autofill Rappresentante dall'anagrafica (come rifCliente).
    // Feature 088: selettore disponibile anche in modifica — i dati derivati vengono
    // sovrascritti SOLO su selezione esplicita dell'operatore, mai al load dell'ordine.
    // Feature 094 (issue #3118): autofill Nota cliente da Anagrafica.nota (come Rappresentante).
    // Feature 101 (issue #3175): autofill Nota consegna da Anagrafica.notaConsegna.
    // Feature 104 (issue #3214): autofill Nota aggiuntiva da Anagrafica.notaAggiuntiva.
    this.impostaCliente(anagrafica.ragioneSociale ?? '', anagrafica.id,
      anagrafica.riferimento ?? '', anagrafica.rappresentante ?? '', anagrafica.nota ?? '',
      anagrafica.notaConsegna ?? '', anagrafica.notaAggiuntiva ?? '');
  }

  // Feature 088 (issue #3061) — valore libero: cliente non presente in anagrafica.
  onRagioneSocialeLibera(testo: string) {
    this.impostaCliente(testo, 0, '', '', '', '', '');
  }

  // Punto unico di scrittura dei dati cliente dell'ordine: al cambio cliente
  // (selezione o valore libero) sede collegata e indirizzo derivato vanno sempre azzerati.
  private impostaCliente(ragioneSociale: string, anagraficaId: number,
                         rifCliente: string, rappresentante: string, notaCliente: string,
                         notaConsegna: string, notaAggiuntiva: string) {
    this.ordine.ragioneSocialeCliente = ragioneSociale;
    this.ordine.anagraficaId          = anagraficaId;
    this.ordine.rifCliente            = rifCliente;
    this.ordine.rappresentante        = rappresentante;
    this.ordine.notaCliente           = notaCliente;
    this.ordine.notaConsegna          = notaConsegna;
    this.ordine.notaAggiuntiva        = notaAggiuntiva;
    this.ordine.sedeConsegnaId        = null;
    this.svuotaIndirizzo();
  }

  onSedeSelezionata(sede: Sede) {
    if (!sede) {
      this.ordine.sedeConsegnaId = null;
      this.svuotaIndirizzo();
      return;
    }
    this.ordine.sedeConsegnaId   = sede.id;
    this.ordine.indirizzo        = sede.indirizzo ?? '';
    this.ordine.citta            = sede.citta     ?? '';
    this.ordine.provincia        = sede.provincia ?? '';
    this.ordine.cap              = sede.cap       ?? '';
    this.ordine.nazione          = sede.nazione   ?? '';
    this.ordine.zona             = sede.zona      ?? '';
    // Feature 079 — auto-fill dei contatti della sede di consegna (restano editabili).
    this.ordine.riferimento      = sede.riferimento ?? '';
    this.ordine.telefono         = sede.telefono    ?? '';
    this.ordine.email            = sede.email       ?? '';
    // Compila anche il campo legacy indirizzoConsegna per retrocompatibilità
    this.ordine.indirizzoConsegna = [sede.indirizzo, sede.citta, sede.provincia]
      .filter(v => !!v).join(', ');
  }

  private svuotaIndirizzo() {
    this.ordine.indirizzo        = '';
    this.ordine.citta            = '';
    this.ordine.provincia        = '';
    this.ordine.cap              = '';
    this.ordine.nazione          = '';
    this.ordine.zona             = '';
    // Feature 079 — azzeramento contatti coerente con l'indirizzo.
    this.ordine.riferimento      = '';
    this.ordine.telefono         = '';
    this.ordine.email            = '';
    this.ordine.indirizzoConsegna = '';
  }

  // ── Materiale ────────────────────────────────────────────────────────────

  loadMateriale() {
    this.loadingMateriale = true;
    this.venditaService
      .getMaterialeOfOrdineAsync(
        this.ordine.organizzazioneId,
        this.ordine.idOrdineVendita,
        this.ordine.idRiga
      )
      .then((list) => (this.materiali = list ?? []))
      .catch(() => this.saveMessage.messageError('Errore nel caricamento del materiale.'))
      .finally(() => (this.loadingMateriale = false));
  }

  async openMaterialeModal(materiale?: MaterialeOrdine) {
    const modal = await this.modalCtrl.create({
      component: MaterialeOrdineCreateComponent,
      componentProps: {
        materiale: materiale ? { ...materiale } : null,
        ordine:    this.ordine,
        isNew:     !materiale
      },
      backdropDismiss: false,
      cssClass: 'custom-modal'
    });
    await modal.present();
    const { role } = await modal.onWillDismiss();
    if (role === 'confirm') {
      this.loadMateriale();
    }
  }

  deleteMateriale(mat: MaterialeOrdine) {
    this.deleteConfirm.presentAlert(
      `Rimuovere il materiale ${mat.codArt} — ${mat.desArticolo}?`,
      () => {
        this.venditaService
          .deleteMaterialeAsync(
            mat.organizzazioneId,
            mat.idOrdineVendita,
            mat.idRigaPadre,
            mat.idRigaMateriale
          )
          .then(() => this.loadMateriale())
          .catch(() => this.saveMessage.messageError('Errore nella rimozione del materiale.'));
      }
    );
  }
}
